/**
 * PostMessage hook for the Card Editor ↔ Storefront card preview.
 *
 * Simplified version of usePageEditorPostMessage:
 * - PREVIEW_READY handshake (queues messages until iframe is ready)
 * - Single method: sendCardTemplate() sends full CardTemplate JSON
 * - Origin validation via isAllowedStorefrontOrigin()
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { isAllowedStorefrontOrigin } from '../mobile-design/preview-protocol'
import type { CardTemplate } from './card-editor-types'

const MSG_PREVIEW_READY = 'PREVIEW_READY'
const MSG_CARD_TEMPLATE = 'widget-preview-card-template'

interface CardEditorMessage {
  type: typeof MSG_CARD_TEMPLATE
  template: CardTemplate
}

export function useCardEditorPostMessage(storefrontUrl: string | null) {
  const [isReady, setIsReady] = useState(false)
  const targetWindowsRef = useRef<Set<Window>>(new Set())
  const targetOriginRef = useRef<string>('')
  const queueRef = useRef<CardEditorMessage[]>([])

  // Extract origin from storefront URL
  useEffect(() => {
    if (storefrontUrl) {
      try {
        targetOriginRef.current = new URL(storefrontUrl).origin
      } catch {
        // Invalid URL — leave origin empty
      }
    }
  }, [storefrontUrl])

  // Listen for PREVIEW_READY from card editor iframe
  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      if (!isAllowedStorefrontOrigin(event.origin, targetOriginRef.current)) return
      if (
        event.data?.type === MSG_PREVIEW_READY &&
        event.data?.source === 'card-editor-preview'
      ) {
        const source = event.source as Window
        targetWindowsRef.current.add(source)
        setIsReady(true)

        // Flush queued messages to the newly-ready window
        const origin = targetOriginRef.current || event.origin
        for (const msg of queueRef.current) {
          source.postMessage(msg, origin)
        }
        queueRef.current = []
      }
    }

    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [])

  const sendCardTemplate = useCallback((template: CardTemplate) => {
    const msg: CardEditorMessage = { type: MSG_CARD_TEMPLATE, template }
    const origin = targetOriginRef.current

    if (targetWindowsRef.current.size > 0 && origin) {
      for (const win of targetWindowsRef.current) {
        try {
          win.postMessage(msg, origin)
        } catch {
          targetWindowsRef.current.delete(win)
        }
      }
    } else {
      queueRef.current = [msg] // Only keep latest — each message is a full snapshot
    }
  }, [])

  return { isReady, sendCardTemplate }
}
